import React, { useEffect, useMemo, useState } from 'react';
import Map, { NavigationControl, ViewStateChangeEvent } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faChevronLeft, faChevronRight, faLocationDot, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { useFetchData } from 'services/api/useFetchData';
import { useSocketChannel } from 'services/api/updatesSocket';
import { useDeepCompareMemo } from 'use-deep-compare';
import { formatDisplayDate } from 'internationalization/timezoneConversions';
import { generateDayKey } from '../functions/schedulingDateUtils';
import { AppointmentEventType, MapAppointmentType, MapEmployeeType, MapToPlanJobType, Period, SchedulerMapDataType } from '../SchedulingTypes';
import EventTooltip from '../EventTooltip';
import EmployeeTooltip from './EmployeeTooltip';
import JobModal from '../../jobs/JobModal';
import MapMarkers from './MapMarkers';
import MultiGroupSelectButton from 'components/buttons/MultiGroupSelectButton';
import { mapboxPlaceholderAccessToken, transformMapboxRequest } from './mapboxProxy';

export interface MapFocusAppointment {
    id: number;
    latitude: string | null;
    longitude: string | null;
}

interface SchedulerMapProps {
    selectedDivisions: number[];
    periodDays: Date[];
    period: Period;
    refreshTick: number;
    focusAppointment: MapFocusAppointment | null;
    fullscreen: boolean;
    onToggleFullscreen: () => void;
}

// Default map center on the Netherlands
const DEFAULT_VIEW_STATE = { longitude: 5.2913, latitude: 52.1326, zoom: 6.5 };

const SchedulerMap: React.FC<SchedulerMapProps> = ({ selectedDivisions, periodDays, period, refreshTick, focusAppointment, fullscreen, onToggleFullscreen }) => {
    const { t } = useTranslation();
    const { userLocale, userTimezone } = useSettings();
    const { initializeModal } = useModal();
    const [selectedFilters, setSelectedFilters] = useState<{ [groupId: string]: (string | number)[] }>({ employees: [], display: [] });
    const [viewState, setViewState] = useState(DEFAULT_VIEW_STATE);
    const [hasCenteredOnCompany, setHasCenteredOnCompany] = useState(false);
    const [handledFocus, setHandledFocus] = useState<MapFocusAppointment | null>(null);
    const [hoveredTooltip, setHoveredTooltip] = useState<{ event: AppointmentEventType, x: number, y: number } | null>(null);
    const [hoveredEmployeeTooltip, setHoveredEmployeeTooltip] = useState<{ employee: MapEmployeeType, x: number, y: number } | null>(null);
    const [employees, setEmployees] = useState<MapEmployeeType[]>([]);
    const [selectedDayIndex, setSelectedDayIndex] = useState(0);

    // Reset the selected day to today when the scheduler period changes, or to the first day
    useEffect(() => {
        const todayKey = generateDayKey(new Date());
        const todayIndex = periodDays.findIndex(day => generateDayKey(day) === todayKey);
        setSelectedDayIndex(todayIndex !== -1 ? todayIndex : 0);
    }, [JSON.stringify(periodDays.map(day => generateDayKey(day)))]);

    // The map always shows the single selected day of the scheduler period
    const displayedDays = useMemo(() => {
        if (periodDays.length === 0) return [];
        return [periodDays[Math.min(selectedDayIndex, periodDays.length - 1)]];
    }, [JSON.stringify(periodDays.map(day => generateDayKey(day))), selectedDayIndex]);

    // Fetch the company address coordinates to center the map on
    const { response: mapCenter } = useFetchData({ apiUrl: 'get_scheduler_map_center', useCache: true });

    // Center the map on the company address once it is known, unless the view was already
    // changed or a specific appointment is being focused via the right click action
    useEffect(() => {
        if (hasCenteredOnCompany || focusAppointment || !mapCenter?.latitude || !mapCenter?.longitude) return;

        setViewState({
            longitude: parseFloat(mapCenter.longitude),
            latitude: parseFloat(mapCenter.latitude),
            zoom: 10
        });
        setHasCenteredOnCompany(true);
    }, [mapCenter, hasCenteredOnCompany, focusAppointment]);

    // Derive the display options from the selected filters
    const hideJobs = (selectedFilters.display ?? []).includes('hide_jobs');
    const showToPlanJobs = (selectedFilters.display ?? []).includes('show_to_plan');
    const includeNew = (selectedFilters.display ?? []).includes('include_new');
    const selectedEmployeeIds = (selectedFilters.employees ?? []).map(Number);

    // Build the fetch params for the map data, based on the scheduler's currently displayed date range
    const fetchParams = useDeepCompareMemo(() => {
        if (!displayedDays || displayedDays.length === 0) return {};

        return {
            from: generateDayKey(displayedDays[0]),
            to: generateDayKey(displayedDays[displayedDays.length - 1]),
            include_new: includeNew ? 'true' : 'false',
            include_to_plan: showToPlanJobs ? 'true' : 'false',
            divisions: selectedDivisions,
        };
    }, [displayedDays, includeNew, showToPlanJobs, selectedDivisions]);

    // Fetch the scheduler map data
    const { response, refetch } = useFetchData({
        apiUrl: Object.keys(fetchParams).length > 0 ? 'get_scheduler_map_data' : '',
        params: fetchParams
    });
    const mapData = response as SchedulerMapDataType | null;

    // Refetch the map data when a relevant job or appointment changes
    const { message: mapSchedulerMessage } = useSocketChannel('map_scheduler', ['map_scheduler_job_updated', 'map_scheduler_appointment_updated']);
    useEffect(() => {
        if (mapSchedulerMessage?.type === 'map_scheduler_job_updated' || mapSchedulerMessage?.type === 'map_scheduler_appointment_updated') {
            refetch();
        }
    }, [mapSchedulerMessage]);

    const appointments = mapData?.appointments ?? [];
    const toPlanJobs = mapData?.to_plan_jobs ?? [];
    const toPlanJobsHiddenCount = mapData?.to_plan_jobs_hidden_count ?? 0;
    const hasManualToScheduleStatus = mapData?.has_manual_to_schedule_status ?? false;

    // Refetch shortly after a response which left locations to geocode, so the pins fill in step by step
    useEffect(() => {
        if (!mapData?.geocoding_pending) return;
        const timer = window.setTimeout(() => refetch(), 500);
        return () => window.clearTimeout(timer);
    }, [mapData]);

    // Filter the appointment pins on the hide jobs option and the selected employees
    const visibleAppointments = useMemo(() => {
        if (hideJobs) return [];
        if (selectedEmployeeIds.length === 0) return appointments;
        return appointments.filter(appointment => appointment.assignees?.some(assignee => selectedEmployeeIds.includes(assignee.id)));
    }, [appointments, hideJobs, JSON.stringify(selectedEmployeeIds)]);

    // Only show the to be planned job pins when that display option is selected
    const visibleToPlanJobs = !hideJobs && showToPlanJobs ? toPlanJobs : [];

    // Collect the jobs which cannot be shown on the map because their location has no coordinates
    const jobsWithoutCoordinates = useMemo(() => {
        const labelsByJobId: Record<number, string> = {};

        // Add the visible appointments without usable coordinates, pending geocodes are not missing yet
        visibleAppointments.forEach(appointment => {
            if (appointment.work_location_latitude && appointment.work_location_longitude) return;
            if (appointment.work_location_geocode_status === 'not_attempted') return;
            labelsByJobId[appointment.job_id] = [appointment.job_number, appointment.job_client_name].filter(Boolean).join(' - ') || String(appointment.job_id);
        });

        // Add the visible to be planned jobs without usable coordinates, pending geocodes are not missing yet
        visibleToPlanJobs.forEach(job => {
            if (job.work_location_latitude && job.work_location_longitude) return;
            if (job.work_location_geocode_status === 'not_attempted') return;
            labelsByJobId[job.id] = [job.number, job.client_name].filter(Boolean).join(' - ') || String(job.id);
        });

        return Object.values(labelsByJobId);
    }, [visibleAppointments, visibleToPlanJobs]);

    // Initialize the employee positions whenever the full map data is (re)fetched
    useEffect(() => {
        if (!mapData) return;
        setEmployees(mapData.employees ?? []);
    }, [mapData]);

    // Determine whether the map shows today, the employee positions are only live for today
    const mapDay = displayedDays.length > 0 ? displayedDays[0] : null;
    const isToday = mapDay ? generateDayKey(mapDay) === generateDayKey(new Date()) : false;

    // Filter the employee markers on the selected employees, only showing them on today
    const visibleEmployees = useMemo(() => {
        if (!isToday) return [];
        if (selectedEmployeeIds.length === 0) return employees;
        return employees.filter(employee => selectedEmployeeIds.includes(employee.user_id));
    }, [employees, isToday, JSON.stringify(selectedEmployeeIds)]);

    // Build the employee filter options from the employees on the map and the appointment assignees
    const employeeOptions = useMemo(() => {
        const namesById: Record<number, string> = {};
        employees.forEach(employee => {
            if (employee.name) namesById[employee.user_id] = employee.name;
        });
        appointments.forEach(appointment => {
            appointment.assignees?.forEach(assignee => {
                if (assignee.name && !(assignee.id in namesById)) namesById[assignee.id] = assignee.name;
            });
        });
        return Object.entries(namesById).map(([value, label]) => ({ value: Number(value), label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [employees, appointments]);

    // Build the filter groups for the map filter button
    const mapFilters = useMemo(() => [
        ...(employeeOptions.length > 0 ? [{
            id: 'employees',
            label: 'scheduling.scheduler_map.employees_filter_label',
            options: employeeOptions,
        }] : []),
        {
            id: 'display',
            label: 'scheduling.scheduler_map.display_filter_label',
            options: [
                { value: 'show_to_plan', label: 'scheduling.scheduler_map.show_to_plan_label' },
                ...(hasManualToScheduleStatus ? [{ value: 'include_new', label: 'scheduling.scheduler_map.include_new_label' }] : []),
                { value: 'hide_jobs', label: 'scheduling.scheduler_map.hide_jobs_label' },
            ],
        },
    ], [employeeOptions, hasManualToScheduleStatus]);

    // Generate the date label shown in the map header
    const dateLabel = mapDay ? (isToday ? t('date_time.relative_days.today') : formatDisplayDate(mapDay, userLocale)) : '';

    // Refetch the map data on the refresh tick of the scheduler event polling, so job,
    // to plan and employee changes reach the map at the same moment as the planning board
    useEffect(() => {
        if (refreshTick > 0) {
            refetch();
        }
    }, [refreshTick]);

    // Zoom to the focused appointment, for the right click "show on map" action. The coordinates
    // are preferably taken from the freshly fetched (and lazily geocoded) map data, falling back
    // to the coordinates known on the scheduler event itself
    useEffect(() => {
        if (!focusAppointment || handledFocus === focusAppointment) return;

        // Prefer the coordinates from the map data, which are geocoded on demand by the backend
        const mapDataAppointment = appointments.find(appointment => appointment.id === focusAppointment.id);
        const latitude = mapDataAppointment?.work_location_latitude ?? focusAppointment.latitude;
        const longitude = mapDataAppointment?.work_location_longitude ?? focusAppointment.longitude;

        // Wait for the map data when no coordinates are known yet
        if (!latitude || !longitude) return;

        // Zoom in on the appointment location while keeping all other pins rendered
        setViewState({
            longitude: parseFloat(String(longitude)),
            latitude: parseFloat(String(latitude)),
            zoom: 13
        });
        setHasCenteredOnCompany(true);
        setHandledFocus(focusAppointment);
    }, [focusAppointment, handledFocus, appointments]);

    // Formats a datetime as a local time string in the appointment's timezone
    const formatTime = (datetime: string, timezone?: string | null) => {
        try {
            return new Intl.DateTimeFormat(userLocale, {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: timezone ?? userTimezone,
            }).format(new Date(datetime));
        } catch {
            return '';
        }
    };

    // Opens the job detail modal for the given job
    const openJobModal = (jobId: number) => {
        initializeModal(React.cloneElement(<JobModal />, { itemId: jobId }), { itemId: jobId, modalSize: 'large' });
    };

    // Builds a scheduler event shaped object for an appointment pin, to reuse the scheduler's event tooltip
    const buildAppointmentTooltipEvent = (appointment: MapAppointmentType): AppointmentEventType => ({
        type: 'appointment',
        key: `map-appointment-${appointment.id}`,
        resourceId: 'map',
        dayKey: '',
        deleted: false,
        appointmentId: appointment.id,
        status: appointment.status,
        custom_color: null,
        locked: false,
        startTime: formatTime(appointment.start_datetime, appointment.timezone),
        endTime: formatTime(appointment.end_datetime, appointment.timezone),
        job: {
            id: appointment.job_id,
            client_name: appointment.job_client_name ?? undefined,
            type_name: appointment.job_type_name ?? undefined,
            number: appointment.job_number ?? null,
            main_description: appointment.job_main_description ?? null,
            status_condition: appointment.job_status_condition ?? null,
            status_name: appointment.job_status_name ?? null,
            labels: appointment.job_labels?.map(label => ({ name: label.name, color: label.color ?? '' })) ?? null,
            work_location_street: appointment.work_location_street ?? undefined,
            work_location_house_number: appointment.work_location_house_number ?? undefined,
            work_location_postal_code: appointment.work_location_postal_code ?? undefined,
            work_location_city: appointment.work_location_city ?? undefined,
        },
    });

    // Builds a scheduler event shaped object for a to be planned job pin, without appointment times
    const buildToPlanJobTooltipEvent = (job: MapToPlanJobType): AppointmentEventType => ({
        type: 'appointment',
        key: `map-to-plan-job-${job.id}`,
        resourceId: 'map',
        dayKey: '',
        deleted: false,
        appointmentId: null,
        status: '',
        custom_color: null,
        locked: false,
        job: {
            id: job.id,
            client_name: job.client_name ?? undefined,
            type_name: job.type_name ?? undefined,
            number: job.number ?? null,
            main_description: job.main_description ?? null,
            status_condition: job.status_condition ?? null,
            status_name: job.status_name ?? null,
            work_location_street: job.work_location_street ?? undefined,
            work_location_house_number: job.work_location_house_number ?? undefined,
            work_location_postal_code: job.work_location_postal_code ?? undefined,
            work_location_city: job.work_location_city ?? undefined,
        },
    });

    return (
        <div className={`scheduler-map ${fullscreen ? 'scheduler-map-fullscreen' : ''}`}>
            <div className='header'>
                <div className='title'>
                    {t('scheduling.scheduler_map.map_title')}
                    <span className='header-date'>{dateLabel}</span>
                </div>
                <div className='controls'>
                    {jobsWithoutCoordinates.length > 0 && (
                        // Indicator with the jobs which are not shown on the map for missing coordinates
                        <div className='scheduler-map-no-address tooltip-icon'>
                            <FontAwesomeIcon icon={faLocationDot} />
                            <span className='no-address-count'>{jobsWithoutCoordinates.length}</span>
                            <span className='tooltip tooltip-bottom tooltip-bottom-right'>
                                <span className='no-address-tooltip-title'>{t('scheduling.scheduler_map.no_address_tooltip_title')}</span>
                                {jobsWithoutCoordinates.map(label => (
                                    <span key={label} className='no-address-tooltip-line'>{label}</span>
                                ))}
                            </span>
                        </div>
                    )}
                    {showToPlanJobs && !hideJobs && toPlanJobsHiddenCount > 0 && (
                        // Indicator with the number of to be planned jobs which fall outside the map limits
                        <div className='scheduler-map-no-address scheduler-map-hidden-to-plan tooltip-icon'>
                            <FontAwesomeIcon icon={faEyeSlash} />
                            <span className='no-address-count'>{toPlanJobsHiddenCount}</span>
                            <span className='tooltip tooltip-bottom tooltip-bottom-right'>
                                {t('scheduling.scheduler_map.hidden_to_plan_tooltip', {
                                    count: toPlanJobsHiddenCount,
                                    weeks: mapData?.to_plan_target_date_window_weeks,
                                    limit: mapData?.to_plan_jobs_without_target_date_limit,
                                })}
                            </span>
                        </div>
                    )}
                    {period === 'week' && periodDays.length > 1 && (
                        // Previous and next day buttons to switch the map day within the scheduler week
                        <div className='map-day-nav'>
                            <button
                                type='button'
                                className='button button-extra-small button-secondary scheduler-map-icon-button'
                                disabled={selectedDayIndex <= 0}
                                onClick={() => setSelectedDayIndex(index => Math.max(0, index - 1))}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <button
                                type='button'
                                className='button button-extra-small button-secondary scheduler-map-icon-button'
                                disabled={selectedDayIndex >= periodDays.length - 1}
                                onClick={() => setSelectedDayIndex(index => Math.min(periodDays.length - 1, index + 1))}
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    )}
                    <MultiGroupSelectButton
                        label="scheduling.scheduler_map.filter_button_label"
                        size="extra-small"
                        groups={mapFilters}
                        selected={selectedFilters}
                        onChange={setSelectedFilters}
                    />
                    <button
                        type='button'
                        className='button button-extra-small button-secondary scheduler-map-icon-button tooltip-icon'
                        onClick={onToggleFullscreen}
                    >
                        <FontAwesomeIcon icon={fullscreen ? faCompress : faExpand} />
                        <span className='tooltip tooltip-bottom tooltip-bottom-right'>
                            {t(fullscreen ? 'scheduling.scheduler_map.exit_fullscreen_tooltip' : 'scheduling.scheduler_map.fullscreen_tooltip')}
                        </span>
                    </button>
                </div>
            </div>

            <div className="scheduler-map-canvas">
                <Map
                    {...viewState}
                    reuseMaps
                    onMove={(evt: ViewStateChangeEvent) => setViewState(evt.viewState)}
                    mapboxAccessToken={mapboxPlaceholderAccessToken}
                    transformRequest={transformMapboxRequest}
                    mapStyle="mapbox://styles/mapbox/streets-v12"
                    style={{ width: '100%', height: '100%' }}
                >
                    <NavigationControl position="top-right" />

                    <MapMarkers
                        appointments={visibleAppointments}
                        toPlanJobs={visibleToPlanJobs}
                        employees={visibleEmployees}
                        focusedAppointmentId={handledFocus?.id ?? null}
                        onClickAppointment={(appointment) => openJobModal(appointment.job_id)}
                        onClickToPlanJob={(job) => openJobModal(job.id)}
                        onHoverAppointment={(appointment, x, y) => setHoveredTooltip(appointment ? { event: buildAppointmentTooltipEvent(appointment), x, y } : null)}
                        onHoverToPlanJob={(job, x, y) => setHoveredTooltip(job ? { event: buildToPlanJobTooltipEvent(job), x, y } : null)}
                        onHoverEmployee={(employee, x, y) => setHoveredEmployeeTooltip(employee ? { employee, x, y } : null)}
                    />
                </Map>

                {hoveredTooltip && (
                    <EventTooltip
                        event={hoveredTooltip.event}
                        x={hoveredTooltip.x}
                        y={hoveredTooltip.y}
                    />
                )}

                {hoveredEmployeeTooltip && (
                    <EmployeeTooltip
                        employee={hoveredEmployeeTooltip.employee}
                        x={hoveredEmployeeTooltip.x}
                        y={hoveredEmployeeTooltip.y}
                    />
                )}
            </div>
        </div>
    );
};

export default SchedulerMap;
